import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useMap } from "react-leaflet";
import axios from "axios";
import L from "leaflet";
import polyline from "@mapbox/polyline";
import { getApiBaseURL } from "../../utils/api";

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const RoutingControl = forwardRef(({ waypoints, onRouteFound }, ref) => {
  const map = useMap(); // gets Leaflet map instance
  const routeLineRef = useRef(null);
  const instructionsRef = useRef(null);
  const apiBase = getApiBaseURL();

  useEffect(() => {
    instructionsRef.current = document.getElementById("routing-instructions");
  }, []);

  useEffect(() => {
    if (!map || waypoints.length < 2) return;

    const stopStrings = waypoints.map((wp) => `${wp.lat},${wp.lng}`);

    axios
      .post(
        `${apiBase}/api/route-calculator/calculate`,
        {
          stops: stopStrings,
        },
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        const {
          distance_miles,
          duration_minutes,
          steps,
          polyline: encodedPolyline,
        } = res.data;

        // Clear previous instructions
        if (instructionsRef.current) {
          instructionsRef.current.innerHTML = "";
          if (Array.isArray(steps)) {
            steps.forEach((step) => {
              const div = document.createElement("div");
              div.innerHTML = step.instruction;
              div.className = "mb-1";
              instructionsRef.current.appendChild(div);
            });
          } else {
            const div = document.createElement("div");
            div.textContent = "No routing steps available.";
            div.className = "text-muted";
            instructionsRef.current.appendChild(div);
          }
        }

        // Remove previous polyline
        if (routeLineRef.current) {
          map.removeLayer(routeLineRef.current);
        }

        // Draw new polyline
        if (encodedPolyline) {
          const latlngs = polyline
            .decode(encodedPolyline)
            .map(([lat, lng]) => ({ lat, lng }));
          const routeLine = L.polyline(latlngs, {
            color: "blue",
            weight: 5,
            opacity: 0.7,
          }).addTo(map);

          routeLineRef.current = routeLine;
          map.fitBounds(routeLine.getBounds());
        }

        onRouteFound(distance_miles, duration_minutes);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          alert("Authentication failed. Please log in again.");
        } else {
          alert("Failed to calculate route using Google Maps.");
        }
      });
  }, [waypoints, onRouteFound, map, apiBase]);

  // Allow parent to clear route manually
  useImperativeHandle(ref, () => ({
    clearRoute: () => {
      if (routeLineRef.current) {
        map.removeLayer(routeLineRef.current);
        routeLineRef.current = null;
      }
      if (instructionsRef.current) {
        instructionsRef.current.innerHTML = "";
      }
    },
  }));

  return null;
});

export default RoutingControl;
